import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';

export type MigrationSourceType = 'csv' | 'google_sheet';

export interface MigrationJobCreateResponse {
  jobId: string;
}

export interface MigrationJobProgress {
  processed?: number;
  total?: number | null;
  inserted?: number;
  updated?: number;
  duplicates?: number;
  invalid?: number;
  failed?: number;
  percent?: number;
  batchSize?: number | null;
  currentBatch?: number;
  totalBatches?: number | null;
  insertBatchesCompleted?: number;
  downloadUrls?: Record<string, string>;
}

export interface MigrationJobProgressResponse {
  jobId: string;
  status: string;
  progress: MigrationJobProgress;
  companyId?: string;
  sourceType?: MigrationSourceType;
  createdAt?: string;
  failedReason?: string | null;
}

/**
 * Paging metadata from `GET .../history`, matching the shape the other paginated
 * admin endpoints return.
 *
 * `total_records` is what lets a caller tell a full page from the end of the
 * data — the migration tab needs that distinction to know whether a vendor's
 * absence from a page means "never synced" or "not on this page".
 */
export interface MigrationHistoryPagination {
  current_page: number;
  per_page: number;
  total_records: number;
  total_pages: number;
}

/** History response: `data` is the row array, with paging alongside it. */
export interface MigrationHistoryResponse extends ApiResponse<MigrationImportHistoryItem[]> {
  pagination?: MigrationHistoryPagination;
}

export interface MigrationImportHistoryItem {
  id: string;
  job_id?: string | null;
  company_name?: string | null;
  actor_name?: string | null;
  status: 'queued' | 'processing' | 'completed' | 'failed' | string;
  source_type?: MigrationSourceType | string;
  processed_count?: number;
  inserted_count?: number;
  duplicate_count?: number;
  invalid_count?: number;
  failed_count?: number;
  result_invalid_s3_url?: string | null;
  result_duplicate_s3_url?: string | null;
  result_failed_s3_url?: string | null;
  source_file_name?: string | null;
  source_file_url?: string | null;
  error_message?: string | null;
  created_at?: string;
  completed_at?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerMigrationImportService {
  private readonly apiService = inject(ApiService);

  private basePath(companyId: string): string {
    return `admin/super-admin/companies/${companyId}/customers/import/migration`;
  }

  downloadTemplate(companyId: string): Observable<Blob> {
    return this.apiService
      .getBlobWithSuperAdminToken(`${this.basePath(companyId)}/template`)
      .pipe(catchError(error => throwError(() => error)));
  }

  createCsvJob(companyId: string, file: File): Observable<ApiResponse<MigrationJobCreateResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', companyId);
    formData.append('sourceType', 'csv');
    return this.apiService
      .postWithSuperAdminToken<MigrationJobCreateResponse>(
        `${this.basePath(companyId)}/jobs`,
        formData
      )
      .pipe(catchError(error => throwError(() => error)));
  }

  createGoogleSheetJob(
    companyId: string,
    sourceId: string,
    options?: { sheetName?: string }
  ): Observable<ApiResponse<MigrationJobCreateResponse>> {
    const payload: Record<string, string | boolean> = {
      companyId,
      sourceType: 'google_sheet',
      sourceId: sourceId.trim(),
    };
    if (options?.sheetName?.trim()) {
      payload['sheetName'] = options.sheetName.trim();
    }
    return this.apiService
      .postWithSuperAdminToken<MigrationJobCreateResponse>(
        `${this.basePath(companyId)}/jobs`,
        payload
      )
      .pipe(catchError(error => throwError(() => error)));
  }

  getJobProgress(
    companyId: string,
    jobId: string
  ): Observable<ApiResponse<MigrationJobProgressResponse>> {
    return this.apiService
      .getWithSuperAdminToken<MigrationJobProgressResponse>(
        `${this.basePath(companyId)}/jobs/${jobId}/progress`
      )
      .pipe(catchError(error => throwError(() => error)));
  }

  /**
   * One page of import history, newest first.
   *
   * The server caps `limit` at 50 and returns `pagination` alongside the rows;
   * `page` is 1-based.
   */
  getHistory(companyId: string, limit = 10, page = 1): Observable<MigrationHistoryResponse> {
    return this.apiService
      .getWithSuperAdminToken<MigrationImportHistoryItem[]>(`${this.basePath(companyId)}/history`, {
        limit,
        page,
      })
      .pipe(catchError(error => throwError(() => error)));
  }

  clearHistory(companyId: string): Observable<ApiResponse<{ deleted: number }>> {
    return this.apiService
      .deleteWithSuperAdminToken<{ deleted: number }>(`${this.basePath(companyId)}/history`)
      .pipe(catchError(error => throwError(() => error)));
  }
}
